/**
 * Hybrid per-tab token storage.
 *
 * Why this exists:
 *  - localStorage is shared across all browser tabs of the same origin, so
 *    when User B logs in in tab 2, every request from tab 1 (still showing
 *    User A) starts using User B's JWT. Cross-tab attribution breaks.
 *  - sessionStorage is scoped to a single tab, but closing the tab loses the
 *    token and forces a re-login on every new tab.
 *
 * Resolution:
 *  - sessionStorage is the authoritative source for this tab.
 *  - On first read in a tab, if sessionStorage is empty we fall back to
 *    localStorage and copy the value into sessionStorage so this tab's session
 *    is now pinned to whoever was logged in when the tab opened.
 *  - On login the tokens are written to BOTH stores so future tabs inherit
 *    the most-recent login.
 *  - On logout we clear both stores. Other already-open tabs keep their own
 *    sessionStorage and continue working as their own user. New tabs opened
 *    after the logout see no inheritance and have to log in.
 */

const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

const hasStorage = () => {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage && !!window.localStorage;
  } catch {
    return false;
  }
};

const readWithFallback = (key) => {
  if (!hasStorage()) return null;
  let value = window.sessionStorage.getItem(key);
  if (value) return value;
  value = window.localStorage.getItem(key);
  if (value) {
    // Pin this tab to the inherited token so a later login in another tab does
    // not silently re-authenticate us as the new user.
    try { window.sessionStorage.setItem(key, value); } catch { /* ignore quota */ }
  }
  return value;
};

export const getAccessToken = () => readWithFallback(ACCESS_KEY);
export const getRefreshToken = () => readWithFallback(REFRESH_KEY);

export const setAccessToken = (access) => {
  if (!hasStorage() || !access) return;
  try {
    window.sessionStorage.setItem(ACCESS_KEY, access);
    window.localStorage.setItem(ACCESS_KEY, access);
  } catch { /* ignore */ }
};

export const setTokens = ({ access, refresh } = {}) => {
  if (!hasStorage()) return;
  try {
    if (access) {
      window.sessionStorage.setItem(ACCESS_KEY, access);
      window.localStorage.setItem(ACCESS_KEY, access);
    }
    if (refresh) {
      window.sessionStorage.setItem(REFRESH_KEY, refresh);
      window.localStorage.setItem(REFRESH_KEY, refresh);
    }
  } catch { /* ignore */ }
};

export const clearTokens = () => {
  if (!hasStorage()) return;
  try {
    window.sessionStorage.removeItem(ACCESS_KEY);
    window.sessionStorage.removeItem(REFRESH_KEY);
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
  } catch { /* ignore */ }
};
